import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** A rule with its latest evaluation; nulls mean it has never been evaluated. */
interface DqRow {
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly dimension: string;
  readonly statusCode: string | null;
  readonly score: number | null;
  readonly lastEvaluatedAt: string | null;
}

const DIMENSIONS = ['', 'COMPLETENESS', 'ACCURACY', 'CONSISTENCY', 'TIMELINESS', 'VALIDITY', 'UNIQUENESS'];
const STATUSES = ['', 'PASS', 'FAIL', 'WARN'];

/**
 * GR L26.04 — Data Quality (`/dwh/data-quality`). The data-quality rule board:
 * every `data_quality_rules` row with its quality dimension and the latest
 * `data_quality_results.status_code` (PASS / FAIL / WARN) and score, so
 * stewards can triage failing rules. Filterable by dimension and result status.
 */
export function GrDataQualityPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<DqRow[]>([]);
  const [dimension, setDimension] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (dimension) params.set('dimension', dimension);
    if (status) params.set('statusCode', status);
    const qs = params.toString();
    apiRequest<{ data: DqRow[] }>(`/v1/dwh/data-quality/rules${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, dimension, status]);

  const failing = rows.filter((r) => r.statusCode === 'FAIL').length;

  return (
    <main aria-label="data-quality">
      <h1>{t('gr26.quality.title')}</h1>
      <div className="filters">
        <label>
          {t('gr26.quality.dimension')}
          <select aria-label="filter-dimension" value={dimension} onChange={(e) => setDimension(e.target.value)}>
            {DIMENSIONS.map((d) => <option key={d || 'all'} value={d}>{d || t('gr26.quality.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr26.quality.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr26.quality.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr26.quality.loading')}</p> : (
        <>
          <p aria-label="rule-count">{t('gr26.quality.count', { count: String(rows.length) })}</p>
          <p aria-label="failing-count">{t('gr26.quality.failing', { count: String(failing) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr26.quality.empty')}</p> : (
            <table aria-label="dq-table">
              <thead><tr><th>{t('gr26.quality.code')}</th><th>{t('gr26.quality.name')}</th><th>{t('gr26.quality.dimension')}</th><th>{t('gr26.quality.status')}</th><th>{t('gr26.quality.score')}</th><th>{t('gr26.quality.evaluated')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.ruleId}>
                    <td>{r.ruleCode}</td><td>{r.ruleName}</td><td>{r.dimension}</td>
                    <td>{r.statusCode ?? '—'}</td><td>{r.score == null ? '—' : `${number(r.score, 1)}%`}</td><td>{at(r.lastEvaluatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
